import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Secao, Vazio, Carregando, Erro, Folha } from '../componentes/ui'
import './caixa.css'

/**
 * Fechamento de caixa e repasse.
 *
 * ─── A REGRA QUE O DESENHO PRECISA IMPOR ───────────────────────────
 *
 * Quem confere digita UM número: o que contou na gaveta. O esperado vem
 * do banco. Num sistema que pede os dois, a diferença é sempre zero
 * porque a pessoa preenche o esperado com o que contou — e o controle
 * deixa de existir exatamente no dia em que faria falta.
 *
 * Por isso o esperado aparece só DEPOIS de a pessoa digitar a contagem.
 * Mostrar antes é entregar a resposta da prova.
 *
 * ─── SOBRE O REPASSE ───────────────────────────────────────────────
 *
 * Duas colunas que parecem a mesma e não são: `devido` é a comissão dos
 * atendimentos feitos; `liberado` é a parte disso que o paciente já
 * pagou de verdade. Só o liberado pode ser repassado — pagar o devido
 * é a clínica financiar o profissional com o próprio capital de giro.
 */

type Conferencia = {
  caixa_id: string; status: string
  aberto_em: string; fechado_em: string | null
  fundo_centavos: string; dinheiro_centavos: string
  esperado_centavos: string; contado_centavos: string | null
  diferenca_calculada: string | null
  pix_centavos: string; cartao_centavos: string; outros_centavos: string
  recebimentos: string
}

type Repasse = {
  profissional_id: string; profissional: string; competencia: string
  atendimentos: number; bruto_centavos: string
  devido_centavos: string; liberado_centavos: string
  pago_centavos: string; a_pagar_centavos: string
  pago_em: string | null; meio: string | null
}

const brl = (c: string | number) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

const mesAtual = () => new Date().toISOString().slice(0, 7)

const mesLongo = (d: string) => {
  const t = new Date(d + 'T12:00:00').toLocaleDateString('pt-BR',
    { month: 'long', year: 'numeric' })
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/** Aceita "1.234,56", "1234,56" e "123456" — devolve centavos. */
function paraCentavos(txt: string): number | null {
  const limpo = txt.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
  if (limpo === '' || limpo === '-') return null
  const n = Number(limpo)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}

/**
 * `foco` deixa o mesmo componente servir a dois destinos da barra
 * lateral — Caixa e Repasse — sem duplicar carregamento nem estado. Sem
 * ele, o repasse continuaria onde estava: no fim de uma página que já
 * era o fim de outra, alcançável só rolando.
 */
export function Caixa({ podeEditar, foco = 'tudo' }: {
  podeEditar: boolean
  foco?: 'tudo' | 'caixa' | 'repasse'
}) {
  const [aberto, setAberto] = useState<Conferencia | null>(null)
  const [historico, setHistorico] = useState<Conferencia[]>([])
  const [repasses, setRepasses] = useState<Repasse[]>([])
  const [mes, setMes] = useState(mesAtual)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [abrindo, setAbrindo] = useState(false)
  const [fechando, setFechando] = useState(false)
  const [pagando, setPagando] = useState<Repasse | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const [c, r] = await Promise.all([
        api<{ aberto: Conferencia | null; historico: Conferencia[] }>('/v1/caixa'),
        api<{ itens: Repasse[] }>(`/v1/repasse?competencia=${mes}`),
      ])
      setAberto(c.aberto); setHistorico(c.historico); setRepasses(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [mes])

  useEffect(() => { void carregar() }, [carregar])

  if (carregando) return <Carregando altura={200} />

  return (
    <>
      <Erro mensagem={erro} />

      {/* ── o caixa de hoje ─────────────────────────────────────── */}
      {foco !== 'repasse' && <Secao
        titulo="Caixa"
        descricao={aberto
          ? `Aberto em ${dataHora(aberto.aberto_em)} · ${aberto.recebimentos} recebimento(s)`
          : 'Nenhum caixa aberto. Recebimento em dinheiro só entra com o caixa aberto.'}
        acao={podeEditar && (aberto
          ? <button className="botao" onClick={() => setFechando(true)}>Fechar caixa</button>
          : <button className="botao" onClick={() => setAbrindo(true)}>Abrir caixa</button>)}
      >
        {aberto ? (
          <div className="cx-grade">
            <Valor rotulo="Fundo de troco" valor={aberto.fundo_centavos} />
            <Valor rotulo="Dinheiro recebido" valor={aberto.dinheiro_centavos} forte />
            <Valor rotulo="Pix" valor={aberto.pix_centavos} />
            <Valor rotulo="Cartão" valor={aberto.cartao_centavos} />
            {Number(aberto.outros_centavos) > 0 && (
              <Valor rotulo="Outros" valor={aberto.outros_centavos} />
            )}
          </div>
        ) : (
          <Vazio
            mensagem="Caixa fechado."
            sugestao="Abra ao começar o expediente. Pix e cartão continuam sendo recebidos normalmente — eles já têm rastro no extrato."
          />
        )}

        {/* O esperado NÃO aparece aqui de propósito. Ver o cabeçalho. */}
        {aberto && (
          <p className="ajuda cx-nota">
            O quanto deveria ter na gaveta aparece na hora de fechar, depois que
            você contar. Mostrar antes seria entregar a resposta.
          </p>
        )}
      </Secao>}

      {/* ── repasse ─────────────────────────────────────────────── */}
      {foco !== 'caixa' && <Secao
        titulo="Repasse aos profissionais"
        descricao="Comissão dos atendimentos concluídos. Só entra no repasse o que o paciente já pagou."
        acao={
          <input className="entrada cx-mes" type="month" value={mes}
                 aria-label="Competência"
                 onChange={(e) => e.target.value && setMes(e.target.value)} />
        }
      >
        {repasses.length === 0 ? (
          <Vazio
            mensagem={`Nenhuma comissão em ${mesLongo(mes + '-01')}.`}
            sugestao="Só aparece aqui quem atendeu procedimento com comissão configurada em A clínica › Catálogo — e cujo atendimento já foi recebido."
          />
        ) : (
          <div className="tabela-rolagem">
            <table className="tabela cx-repasse">
              <thead>
                <tr>
                  <th>Profissional</th>
                  <th className="num">Atend.</th>
                  <th className="num">Faturou</th>
                  <th className="num">Devido</th>
                  <th className="num">Liberado</th>
                  <th className="num">Pago</th>
                  <th className="num">A pagar</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {repasses.map((r) => (
                  <tr key={r.profissional_id}>
                    <td>{r.profissional}</td>
                    <td className="num mono">{r.atendimentos}</td>
                    <td className="num mono">{brl(r.bruto_centavos)}</td>
                    <td className="num mono">{brl(r.devido_centavos)}</td>
                    {/* Liberado < devido é o normal, não um erro: é o que
                        o paciente ainda não pagou. A cor âmbar sinaliza
                        sem acusar. */}
                    <td className={`num mono ${
                      Number(r.liberado_centavos) < Number(r.devido_centavos)
                        ? 'cx-parcial' : ''}`}>
                      {brl(r.liberado_centavos)}
                    </td>
                    <td className="num mono">{brl(r.pago_centavos)}</td>
                    <td className="num mono cx-apagar">{brl(r.a_pagar_centavos)}</td>
                    <td className="num">
                      {r.pago_em ? (
                        <span className="selo selo--ok">pago</span>
                      ) : podeEditar && Number(r.a_pagar_centavos) > 0 ? (
                        <button className="cx-pagar" onClick={() => setPagando(r)}>
                          Pagar
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Secao>}

      {/* ── histórico ───────────────────────────────────────────── */}
      {foco !== 'repasse' && historico.length > 0 && (
        <Secao titulo="Fechamentos anteriores"
               descricao="Um caixa fechado não se reabre. Ajuste só por estorno na sessão corrente.">
          <ul className="cx-historico">
            {historico.map((h) => (
              <li key={h.caixa_id} className={h.status === 'DIVERGENTE' ? 'is-divergente' : ''}>
                <span className="cx-historico__data mono">
                  {h.fechado_em ? dataHora(h.fechado_em) : dataHora(h.aberto_em)}
                </span>
                <span className="cx-historico__mov">
                  {h.recebimentos} recebimento(s) · gaveta {brl(h.esperado_centavos)}
                </span>
                {h.diferenca_calculada !== null && Number(h.diferenca_calculada) !== 0 ? (
                  <span className="cx-dif mono">
                    {Number(h.diferenca_calculada) > 0 ? 'sobrou ' : 'faltou '}
                    {brl(Math.abs(Number(h.diferenca_calculada)))}
                  </span>
                ) : (
                  <span className="cx-bateu">bateu</span>
                )}
              </li>
            ))}
          </ul>
        </Secao>
      )}

      {abrindo && (
        <AbrirCaixa aoFechar={() => setAbrindo(false)}
                    aoAbrir={async () => { setAbrindo(false); await carregar() }} />
      )}

      {fechando && aberto && (
        <FecharCaixa caixa={aberto} aoFechar={() => setFechando(false)}
                     aoConcluir={async () => { setFechando(false); await carregar() }} />
      )}

      {pagando && (
        <PagarRepasse r={pagando} aoFechar={() => setPagando(null)}
                      aoPagar={async () => { setPagando(null); await carregar() }} />
      )}
    </>
  )
}

function Valor({ rotulo, valor, forte = false }: {
  rotulo: string; valor: string; forte?: boolean
}) {
  return (
    <div className={`cx-valor ${forte ? 'is-forte' : ''}`}>
      <span>{rotulo}</span>
      <strong className="mono">{brl(valor)}</strong>
    </div>
  )
}

/* ══ abrir ═════════════════════════════════════════════════════════ */

function AbrirCaixa({ aoFechar, aoAbrir }: {
  aoFechar: () => void; aoAbrir: () => Promise<void>
}) {
  const [fundo, setFundo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  async function abrir(e: React.FormEvent) {
    e.preventDefault()
    const c = fundo.trim() === '' ? 0 : paraCentavos(fundo)
    if (c === null) { setErro('Valor inválido.'); return }
    setOcupado(true); setErro(null)
    try {
      await api('/v1/caixa/abrir', { metodo: 'POST', corpo: { fundo_centavos: c } })
      await aoAbrir()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível abrir.')
      setOcupado(false)
    }
  }

  return (
    <Folha titulo="Abrir caixa"
           descricao="O fundo de troco entra no esperado do fechamento."
           aoFechar={aoFechar}
           acoes={
             <button type="submit" form="form-abrir" className="botao" disabled={ocupado}>
               {ocupado ? 'Abrindo…' : 'Abrir'}
             </button>
           }>
      <form id="form-abrir" onSubmit={abrir} className="cx-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}
        <div className="campo">
          <label htmlFor="cx-fundo">Fundo de troco</label>
          <input id="cx-fundo" className="entrada mono" inputMode="decimal"
                 placeholder="0,00" autoFocus
                 value={fundo} onChange={(e) => setFundo(e.target.value)} />
          <span className="ajuda">
            Quanto tem na gaveta agora, antes do primeiro atendimento. Deixe vazio
            se começa zerado.
          </span>
        </div>
      </form>
    </Folha>
  )
}

/* ══ fechar ════════════════════════════════════════════════════════ */

/**
 * A conferência.
 *
 * O esperado só aparece depois que a pessoa digita a contagem. Não é
 * teatro: um campo "esperado" visível ao lado de um campo "contado"
 * vazio é um convite a copiar um no outro, e aí a diferença nunca
 * existe.
 */
function FecharCaixa({ caixa, aoFechar, aoConcluir }: {
  caixa: Conferencia; aoFechar: () => void; aoConcluir: () => Promise<void>
}) {
  const [contado, setContado] = useState('')
  const [justificativa, setJustificativa] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const centavos = paraCentavos(contado)
  const esperado = Number(caixa.esperado_centavos)
  const diferenca = centavos === null ? null : centavos - esperado
  const bate = diferenca === 0

  async function fechar(e: React.FormEvent) {
    e.preventDefault()
    if (centavos === null) { setErro('Informe quanto contou na gaveta.'); return }
    if (diferenca !== 0 && justificativa.trim() === '') {
      setErro('Caixa com diferença precisa de justificativa.'); return
    }
    setOcupado(true); setErro(null)
    try {
      await api(`/v1/caixa/${caixa.caixa_id}/fechar`, {
        metodo: 'POST',
        corpo: {
          contado_centavos: centavos,
          ...(justificativa.trim() ? { justificativa: justificativa.trim() } : {}),
        },
      })
      await aoConcluir()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível fechar.')
      setOcupado(false)
    }
  }

  return (
    <Folha titulo="Fechar caixa"
           descricao="Conte o dinheiro em espécie da gaveta e informe o total."
           aoFechar={aoFechar}
           acoes={
             <button type="submit" form="form-fechar" className="botao"
                     disabled={ocupado || centavos === null}>
               {ocupado ? 'Fechando…' : bate ? 'Fechar caixa' : 'Fechar com diferença'}
             </button>
           }>
      <form id="form-fechar" onSubmit={fechar} className="cx-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="cx-contado">Quanto tem na gaveta</label>
          <input id="cx-contado" className="entrada mono cx-contagem"
                 inputMode="decimal" placeholder="0,00" autoFocus
                 value={contado} onChange={(e) => setContado(e.target.value)} />
          <span className="ajuda">Só dinheiro em espécie. Cartão e Pix não entram.</span>
        </div>

        {/* Só depois da contagem. */}
        {centavos !== null && (
          <div className={`cx-resultado ${bate ? 'is-ok' : 'is-dif'}`}>
            <div>
              <span>Deveria ter</span>
              <strong className="mono">{brl(esperado)}</strong>
              <span className="ajuda">
                fundo {brl(caixa.fundo_centavos)} + {brl(caixa.dinheiro_centavos)} recebidos
              </span>
            </div>
            <div>
              <span>Diferença</span>
              <strong className="mono">
                {diferenca === 0 ? 'bateu'
                  : `${diferenca! > 0 ? '+' : '−'} ${brl(Math.abs(diferenca!))}`}
              </strong>
            </div>
          </div>
        )}

        {centavos !== null && !bate && (
          <div className="campo">
            <label htmlFor="cx-just">O que aconteceu</label>
            <textarea id="cx-just" className="entrada" rows={2} maxLength={500}
                      value={justificativa}
                      onChange={(e) => setJustificativa(e.target.value)} />
            <span className="ajuda">
              Obrigatório quando não bate. O caixa fecha como divergente e a
              diferença fica registrada — deixar passar em silêncio é o mesmo
              que não conferir.
            </span>
          </div>
        )}
      </form>
    </Folha>
  )
}

/* ══ pagar repasse ═════════════════════════════════════════════════ */

function PagarRepasse({ r, aoFechar, aoPagar }: {
  r: Repasse; aoFechar: () => void; aoPagar: () => Promise<void>
}) {
  const maximo = Number(r.a_pagar_centavos)
  const [valor, setValor] = useState(() => (maximo / 100).toFixed(2).replace('.', ','))
  const [meio, setMeio] = useState('PIX')
  const [obs, setObs] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const centavos = paraCentavos(valor)

  async function pagar(e: React.FormEvent) {
    e.preventDefault()
    if (centavos === null || centavos <= 0) { setErro('Valor inválido.'); return }
    if (centavos > maximo) {
      setErro(`O máximo liberado é ${brl(maximo)} — o resto o paciente ainda não pagou.`)
      return
    }
    setOcupado(true); setErro(null)
    try {
      await api('/v1/repasse', {
        metodo: 'POST',
        corpo: {
          profissional_id: r.profissional_id,
          competencia: r.competencia.slice(0, 10),
          valor_centavos: centavos, meio,
          ...(obs.trim() ? { observacao: obs.trim() } : {}),
        },
      })
      await aoPagar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível registrar.')
      setOcupado(false)
    }
  }

  return (
    <Folha titulo={`Repasse · ${r.profissional}`}
           descricao={`${mesLongo(r.competencia.slice(0, 10))} · ${r.atendimentos} atendimento(s)`}
           aoFechar={aoFechar}
           acoes={
             <button type="submit" form="form-repasse" className="botao" disabled={ocupado}>
               {ocupado ? 'Registrando…' : 'Registrar pagamento'}
             </button>
           }>
      <form id="form-repasse" onSubmit={pagar} className="cx-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="cx-grade">
          <Valor rotulo="Comissão do período" valor={r.devido_centavos} />
          <Valor rotulo="Liberado (o paciente pagou)" valor={r.liberado_centavos} forte />
        </div>

        {Number(r.liberado_centavos) < Number(r.devido_centavos) && (
          <p className="ajuda cx-nota">
            {brl(Number(r.devido_centavos) - Number(r.liberado_centavos))} ainda não
            entrou: são atendimentos feitos que o paciente não pagou. Entram no
            repasse assim que o dinheiro cair.
          </p>
        )}

        <div className="campo">
          <label htmlFor="rp-valor">Valor a repassar</label>
          <input id="rp-valor" className="entrada mono" inputMode="decimal" autoFocus
                 value={valor} onChange={(e) => setValor(e.target.value)} />
          <span className="ajuda">No máximo {brl(maximo)}.</span>
        </div>

        <div className="campo">
          <label htmlFor="rp-meio">Como pagou</label>
          <select id="rp-meio" className="entrada" value={meio}
                  onChange={(e) => setMeio(e.target.value)}>
            <option value="PIX">Pix</option>
            <option value="TRANSFERENCIA">Transferência</option>
            <option value="DINHEIRO">Dinheiro</option>
            <option value="OUTRO">Outro</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="rp-obs">Observação</label>
          <input id="rp-obs" className="entrada" maxLength={300}
                 value={obs} onChange={(e) => setObs(e.target.value)} />
        </div>
      </form>
    </Folha>
  )
}
